// Which of the three ways of reading a Chapter the Author last chose.
//
// There is one Author behind the password gate and no Author record to hang a
// preference on (DESIGN.md "Two languages, kept apart" makes the same point
// about the Interface Language), so the browser is where the choice lives.
// localStorage rather than the tab's session storage: the Author who reads
// script-only comes back to script-only tomorrow.

export const VIEW_MODES = ["sideBySide", "inline", "scriptOnly"] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

export const DEFAULT_VIEW_MODE: ViewMode = "sideBySide";
export const VIEW_MODE_STORAGE_KEY = "b2a_review_view";

/** The digit that selects a mode, in the order the header shows them. */
export const VIEW_MODE_DIGITS: Readonly<Record<string, ViewMode>> = Object.freeze({
  "1": "sideBySide",
  "2": "inline",
  "3": "scriptOnly",
});

const isViewMode = (value: string | null): value is ViewMode =>
  value !== null && (VIEW_MODES as readonly string[]).includes(value);

/**
 * What to open on. Storage is read defensively because Safari throws on
 * `localStorage` in a private window rather than returning null, and a
 * preference is never worth a blank screen.
 */
export function readViewMode(storage: Pick<Storage, "getItem"> | undefined): ViewMode {
  try {
    const stored = storage?.getItem(VIEW_MODE_STORAGE_KEY) ?? null;
    return isViewMode(stored) ? stored : DEFAULT_VIEW_MODE;
  } catch {
    return DEFAULT_VIEW_MODE;
  }
}

/** Remembers the choice; a storage that refuses costs the memory, not the switch. */
export function writeViewMode(storage: Pick<Storage, "setItem"> | undefined, mode: ViewMode): void {
  try {
    storage?.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // A full or blocked storage is not a reason to refuse the Author the mode.
  }
}

/** True when the mode shows the Manuscript column at all. */
export const showsManuscript = (mode: ViewMode): boolean => mode === "sideBySide";
